import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { CreateProjectProductInput, Product, ProjectProduct } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal } from '../../components/ui/Modal.js';
import { useProductOptions } from '../products/api.js';
import { formatProductLabel } from '../products/format.js';
import { ProductPicker } from '../products/ProductPicker.js';
import {
  useCreateProjectProduct,
  useDeleteProjectProduct,
  useUpdateProjectProduct,
} from './api.js';

interface ProjectProductFormModalProps {
  isOpen: boolean;
  projectId: string;
  /** Existing line to edit, or null to attach a new product. */
  projectProduct: ProjectProduct | null;
  /** Product ids already attached to the project (excluded from the picker on create). */
  attachedProductIds: string[];
  onClose: () => void;
}

interface FormState {
  productId: string;
  quantity: string;
  unitPrice: string;
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function ProjectProductFormModal({
  isOpen,
  projectId,
  projectProduct: projectProductProp,
  attachedProductIds,
  onClose,
}: ProjectProductFormModalProps): JSX.Element {
  const [projectProduct, setProjectProduct] = useState<ProjectProduct | null>(projectProductProp);
  const isEdit = projectProduct !== null;
  const { isAdmin } = useAuth();
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>({ productId: '', quantity: '1', unitPrice: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateProjectProduct(projectId);
  const updateMutation = useUpdateProjectProduct(projectId);
  const deleteMutation = useDeleteProjectProduct(projectId);
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  const productsQuery = useProductOptions();
  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data]);
  const productsById = useMemo(() => {
    const map = new Map<string, Product>();
    for (const product of products) map.set(product.id, product);
    return map;
  }, [products]);

  const productOptions = useMemo(
    () => products.filter((product) => !attachedProductIds.includes(product.id)),
    [products, attachedProductIds],
  );

  useEffect(() => {
    if (isOpen) {
      setProjectProduct(projectProductProp);
    }
  }, [isOpen, projectProductProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(
        projectProduct
          ? {
              productId: projectProduct.productId,
              quantity: projectProduct.quantity,
              unitPrice: projectProduct.unitPrice,
            }
          : { productId: '', quantity: '1', unitPrice: '' },
      );
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, projectProduct, resetSaveMode, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save product.'
        : null;

  const handleProductChange = (productId: string): void => {
    const product = productsById.get(productId);
    setForm((f) => ({
      ...f,
      productId,
      // Default the override price to the catalog price when picking a product.
      unitPrice:
        f.unitPrice && f.productId === productId ? f.unitPrice : (product?.unitPrice ?? ''),
    }));
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (isEdit) {
      updateMutation.mutate(
        {
          id: projectProduct.id,
          input: { quantity: form.quantity.trim(), unitPrice: form.unitPrice.trim() },
        },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      const payload: CreateProjectProductInput = {
        productId: form.productId,
        quantity: form.quantity.trim(),
        unitPrice: form.unitPrice.trim(),
      };
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setProjectProduct(created);
          }
        },
      });
    }
  };

  const editingProduct = projectProduct ? productsById.get(projectProduct.productId) : undefined;

  const handleDelete = (): void => {
    if (!projectProduct) {
      return;
    }
    deleteMutation.mutate(projectProduct.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit product' : 'Attach product'}
        footer={
          <FormActions
            formId="project-product-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit && isAdmin ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Remove
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="project-product-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          {isEdit ? (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-slate-700">Product</span>
              <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                {editingProduct ? formatProductLabel(editingProduct) : '—'}
              </span>
            </div>
          ) : (
          <ProductPicker
            label="Product"
            required
            products={productOptions}
            value={form.productId}
            placeholder={productsQuery.isLoading ? 'Loading…' : undefined}
            onChange={handleProductChange}
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="Quantity"
              required
              type="number"
              min={0}
              step="0.0001"
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              error={fieldErrors.quantity}
            />
            <TextField
              label="Unit price"
              required
              type="number"
              min={0}
              step="0.0001"
              value={form.unitPrice}
              onChange={(e) => setForm((f) => ({ ...f, unitPrice: e.target.value }))}
              error={fieldErrors.unitPrice}
            />
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Remove product"
        message="Remove this product from the project?"
        confirmLabel="Remove"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
